import {useEffect, useRef, useState} from 'react';
import type {BanInfo, Friend, FriendData, ProfileSummary, SteamID64, SteamPlayer} from '../types/steam';
import {BanCount as toBanCount, Days as toDays, Minutes as toMinutes, VanityName as toVanityName} from '../types/steam';
import {normalizeRecentNames} from '../utils/dateUtils';
import {convertToSteamId64, parseSteamInput} from '../utils/steamParser';
import {
    getBansBatch,
    getCS2Playtime,
    getFriendsList,
    getPlayerBans,
    getRecentNames,
    getPlayersBatch,
    getPlayerSummary,
    resolveVanityUrl
} from '../utils/steamApi';
import {getFriends as getCachedFriends, setFriend as setCachedFriend} from '../utils/cache';

/**
 * Extract Steam ID from URL path, supporting multiple formats:
 * - /{steamid} - direct Steam ID
 * - /id/{vanityname} or /id/{vanityname}/ - Steam vanity URL format
 * - /profiles/{steamid64} or /profiles/{steamid64}/ - Steam profile URL format
 */
function extractSteamIdFromPath(path: string): string | null {
    // Remove trailing slash for easier matching
    const cleanPath = path.endsWith('/') ? path.slice(0, -1) : path;

    // Match /id/vanityname format
    const vanityMatch = cleanPath.match(/^\/id\/([^\/]+)$/);
    if (vanityMatch && vanityMatch[1]) {
        return vanityMatch[1];
    }

    // Match /profiles/steamid64 format
    const profilesMatch = cleanPath.match(/^\/profiles\/([^\/]+)$/);
    if (profilesMatch && profilesMatch[1]) {
        return profilesMatch[1];
    }

    // Match single segment format /{steamid}
    const singleMatch = cleanPath.match(/^\/([^\/]+)$/);
    if (singleMatch && singleMatch[1] && singleMatch[1] !== 'index.html') {
        return singleMatch[1];
    }

    return null;
}

interface SearchFormProps {
    status: { message: string; kind: 'info' | 'warn' | 'error' };
    onStatusChange: (status: { message: string; kind: 'info' | 'warn' | 'error' }) => void;
    onProfileLoad: (summary: ProfileSummary) => void;
    onFriendsLoad: (friends: FriendData[]) => void;
    onLoadingStart: () => void;
}

function SearchForm({status, onStatusChange, onProfileLoad, onFriendsLoad, onLoadingStart}: SearchFormProps) {
    const [input, setInput] = useState<string>('');
    const [isChecking, setIsChecking] = useState<boolean>(false);
    const hasAutoChecked = useRef<boolean>(false);

    useEffect(() => {
        // Handle deep-linking on initial load
        if (!hasAutoChecked.current) {
            const steamId = extractSteamIdFromPath(window.location.pathname);
            if (steamId) {
                setInput(steamId);
                hasAutoChecked.current = true;
                // Trigger check after a short delay to ensure state is set
                setTimeout(() => doCheck(steamId), 100);
            }
        }
    }, []);

    useEffect(() => {
        // Handle browser back/forward navigation
        const handlePopState = () => {
            const steamId = extractSteamIdFromPath(window.location.pathname);
            if (steamId) {
                setInput(steamId);
                doCheck(steamId);
            } else {
                // Navigated to root - clear the form
                setInput('');
                onStatusChange({message: '—', kind: 'info'});
                onProfileLoad(null as any);
                onFriendsLoad([]);
            }
        };

        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, []);

    const doCheck = async (inputValue?: string): Promise<void> => {
        const valueToCheck: string = inputValue || input;
        if (isChecking || !valueToCheck.trim()) return;

        try {
            setIsChecking(true);
            onLoadingStart();
            onStatusChange({message: 'Parsing input...', kind: 'info'});

            const parsed = parseSteamInput(valueToCheck);
            let steamId64: SteamID64;

            if (parsed.type === 'vanity') {
                onStatusChange({message: 'Resolving vanity name...', kind: 'info'});
                steamId64 = await resolveVanityUrl(toVanityName(parsed.value));
            } else {
                steamId64 = convertToSteamId64(parsed);
            }

            onStatusChange({message: 'Fetching profile...', kind: 'info'});
            const [player, bans, cs2Playtime, recentNames]: [SteamPlayer, BanInfo, number | undefined, string[]] = await Promise.all([
                getPlayerSummary(steamId64),
                getPlayerBans(steamId64),
                getCS2Playtime(steamId64),
                getRecentNames(steamId64),
            ]);
            const normalizedRecentNames = normalizeRecentNames(recentNames, player.personaname);

            const cs2PlaytimeTyped = cs2Playtime !== undefined ? toMinutes(cs2Playtime) : undefined;

            onStatusChange({message: 'Fetching friends list...', kind: 'info'});
            let friendsList: Friend[];
            try {
                friendsList = await getFriendsList(steamId64);
            } catch (err: unknown) {
                onStatusChange({message: (err as Error).message, kind: 'warn'});
                friendsList = [];
            }

            const summary: ProfileSummary = {
                id64: steamId64,
                persona: player.personaname,
                profileUrl: player.profileurl,
                recentNames: normalizedRecentNames,
                vacBanned: bans.VACBanned,
                vacBans: bans.NumberOfVACBans,
                daysSinceLastBan: bans.DaysSinceLastBan,
                gameBans: bans.NumberOfGameBans,
                timeCreated: player.timecreated,
                friendCount: friendsList.length,
                friendVacCount: toBanCount(0),
                friendGameCount: toBanCount(0),
                cs2PlaytimeMinutes: cs2PlaytimeTyped,
            };

            onProfileLoad(summary);

            if (friendsList.length > 0) {
                onStatusChange({message: `Loading ${friendsList.length} friends...`, kind: 'info'});

                const friendIds: SteamID64[] = friendsList.map((f: Friend): SteamID64 => f.steamid);
                const {cached, missing} = getCachedFriends(friendIds);

                const friendsData: FriendData[] = Object.values(cached);

                if (missing.length > 0) {
                    onStatusChange({message: `Fetching ${missing.length} friend details...`, kind: 'info'});

                    const [players, bansList]: [SteamPlayer[], BanInfo[]] = await Promise.all([
                        getPlayersBatch(missing),
                        getBansBatch(missing),
                    ]);

                    const playerMap: Map<SteamID64, SteamPlayer> = new Map(players.map((p: SteamPlayer) => [
                        p.steamid,
                        p
                    ]));
                    const bansMap: Map<SteamID64, BanInfo> = new Map(bansList.map((b: BanInfo) => [b.SteamId, b]));

                    for (const id of missing) {
                        const player: SteamPlayer | undefined = playerMap.get(id);
                        const bans: BanInfo | undefined = bansMap.get(id);

                        // Use player data from API if available, otherwise create fallback
                        // If player exists but is missing personaname, default to 'Unknown'
                        const friendPlayer: SteamPlayer = player ? {
                            ...player,
                            personaname: player.personaname || 'Unknown',
                        } : {
                            steamid: id,
                            personaname: 'Unknown',
                            profileurl: `https://steamcommunity.com/profiles/${id}`,
                            avatar: '',
                            avatarmedium: '',
                            avatarfull: '',
                        };

                        const friendBans: BanInfo = bans || {
                            SteamId: id,
                            CommunityBanned: false,
                            VACBanned: false,
                            NumberOfVACBans: toBanCount(0),
                            DaysSinceLastBan: toDays(0),
                            NumberOfGameBans: toBanCount(0),
                            EconomyBan: 'none',
                        };

                        const friendData: FriendData = {player: friendPlayer, bans: friendBans};
                        friendsData.push(friendData);
                        setCachedFriend(id, friendData);
                    }
                }

                const vacCount: number = friendsData.filter((f: FriendData): boolean => f.bans.VACBanned).length;
                const gameCount: number = friendsData.filter((f: FriendData): boolean => f.bans.NumberOfGameBans >
                    0).length;

                summary.friendVacCount = toBanCount(vacCount);
                summary.friendGameCount = toBanCount(gameCount);
                onProfileLoad(summary);
                onFriendsLoad(friendsData);

                onStatusChange({
                    message: 'Done.',
                    kind: 'info'
                });
            } else {
                onFriendsLoad([]);
                onStatusChange({message: 'Done.', kind: 'info'});
            }
        } catch (err: unknown) {
            onStatusChange({message: (err as Error).message, kind: 'error'});
        } finally {
            setIsChecking(false);
        }
    };

    const handleCheck = () => {
        doCheck();
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleCheck();
            (e.target as HTMLElement).blur();
        }
    };
    return (
        <section className="card p-4 sm:p-6 space-y-2 sm:space-y-2">
            <div>
                <label className="block">
                    <span className="text-sm font-medium mb-1 block">Steam Profile (name, URL, or ID)</span>
                    <div className="flex gap-2">
                        <input
                            id="profileInput"
                            type="text"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder="e.g. gabelogannewell, https://steamcommunity.com/id/yourname, 76561198000000000, [U:1:123456], STEAM_1:1:123456"
                            className="input-field"
                        />
                        <button
                            onClick={handleCheck}
                            disabled={isChecking}
                            className="btn-primary"
                        >
                            {isChecking ? 'Checking...' : 'Check'}
                        </button>
                    </div>
                </label>
            </div>

            <div className="text-sm text-secondary">
        <span
            className={status.kind === 'error' ? 'text-rose-700 dark:text-rose-400' : status.kind === 'warn'
                ? 'text-amber-700 dark:text-amber-400'
                : ''}>
          {status.message}
        </span>
            </div>
        </section>
    );
}

export default SearchForm;
